"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { resetAnalyticsIdentity } from "@/shared/analytics/events";
import { fetchCurrentUser, loginV2, logoutRequest, toAuthError } from "@/shared/auth/api/auth";
import {
  clearStoredAuthSession,
  clearStoredAuthToken,
  formatAuthUserName,
  getStoredAuthSession,
  setStoredAuthSession,
  subscribeToAuthSessionExpiration,
  type AuthSession,
} from "@/shared/auth/session";
import { AUTH_STORAGE_KEY } from "@/shared/config/constants";

type LoginPayload = {
  phone: string;
  password: string;
};

type AuthContextValue = {
  session: AuthSession | null;
  user: AuthSession["user"] | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  sessionError: boolean;
  retrySession: () => void;
  queryScope: number;
  login: (payload: LoginPayload) => Promise<AuthSession>;
  isLoginPending: boolean;
  loginError: string | null;
  resetLoginError: () => void;
  applySession: (session: AuthSession) => void;
  updateUser: (patch: Partial<AuthSession["user"]>) => void;
  logout: () => Promise<void>;
  registerBeforeAuthChange: (callback: () => void | Promise<void>) => () => void;
  registerBeforeOrganizationChange: (callback: () => void | Promise<void>) => () => void;
  prepareForOrganizationChange: () => Promise<void>;
  displayName: string;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function hasSameSessionScope(left: AuthSession | null, right: AuthSession | null) {
  return (
    left?.user.id === right?.user.id && (left?.user.organization_id ?? null) === (right?.user.organization_id ?? null)
  );
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [{ session, queryScope }, setAuthState] = useState<{ session: AuthSession | null; queryScope: number }>({
    session: null,
    queryScope: 0,
  });
  const sessionRef = useRef<AuthSession | null>(null);
  // A transient check can hide the session before confirmed expiration resets its analytics identity.
  const hasAnalyticsSessionRef = useRef(false);
  const setSession = useCallback((nextSession: AuthSession | null) => {
    sessionRef.current = nextSession;
    if (nextSession) hasAnalyticsSessionRef.current = true;
    setAuthState((current) => {
      // Initial sign-in keeps the auth form's pending registration/retry state.
      const scopeChanged = Boolean(current.session) && !hasSameSessionScope(current.session, nextSession);
      return { session: nextSession, queryScope: current.queryScope + Number(scopeChanged) };
    });
  }, []);
  const persistSession = useCallback(
    (nextSession: AuthSession) => {
      const previous = sessionRef.current ?? getStoredAuthSession();
      if (previous && previous.user.id !== nextSession.user.id) clearStoredAuthToken();
      setStoredAuthSession(nextSession);
      setSession(nextSession);
    },
    [setSession],
  );
  const [isHydrated, setIsHydrated] = useState(false);
  const [sessionError, setSessionError] = useState(false);
  const [loginState, setLoginState] = useState<{ isPending: boolean; error: unknown }>({
    isPending: false,
    error: null,
  });
  const loginRevisionRef = useRef(0);
  const authRevisionRef = useRef(0);
  const beforeAuthChangeRef = useRef(new Set<() => void | Promise<void>>());
  const beforeOrganizationChangeRef = useRef(new Set<() => void | Promise<void>>());

  const resetSessionAnalytics = useCallback(() => {
    if (!hasAnalyticsSessionRef.current) return;
    hasAnalyticsSessionRef.current = false;
    resetAnalyticsIdentity();
  }, []);

  const notifyBeforeAuthChange = useCallback(async () => {
    const callbacks = Array.from(beforeAuthChangeRef.current, (callback) => {
      try {
        return Promise.resolve(callback());
      } catch (error) {
        return Promise.reject(error);
      }
    });
    await Promise.allSettled(callbacks);
  }, []);

  const registerBeforeAuthChange = useCallback((callback: () => void | Promise<void>) => {
    beforeAuthChangeRef.current.add(callback);
    return () => {
      beforeAuthChangeRef.current.delete(callback);
    };
  }, []);

  const registerBeforeOrganizationChange = useCallback((callback: () => void | Promise<void>) => {
    beforeOrganizationChangeRef.current.add(callback);
    return () => {
      beforeOrganizationChangeRef.current.delete(callback);
    };
  }, []);

  const prepareForOrganizationChange = useCallback(async () => {
    // Usage is best-effort: do not make organization changes depend indefinitely
    // on a network heartbeat. The server rejects a late old-org heartbeat.
    const callbacks = Array.from(beforeOrganizationChangeRef.current, (callback) => {
      try {
        return Promise.resolve(callback());
      } catch (error) {
        return Promise.reject(error);
      }
    });
    await Promise.race([
      Promise.allSettled(callbacks),
      new Promise<void>((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  }, []);

  useEffect(
    () =>
      subscribeToAuthSessionExpiration(() => {
        authRevisionRef.current += 1;
        setSession(null);
        setIsHydrated(true);
        setSessionError(false);
        resetSessionAnalytics();
        void notifyBeforeAuthChange();
      }),
    [notifyBeforeAuthChange, resetSessionAnalytics, setSession],
  );

  const refreshSession = useCallback(() => {
    const revision = ++authRevisionRef.current;
    setIsHydrated(false);
    setSessionError(false);
    fetchCurrentUser()
      .then((user) => {
        if (revision !== authRevisionRef.current) return;
        persistSession({ user });
        setIsHydrated(true);
      })
      .catch((error: unknown) => {
        if (revision !== authRevisionRef.current) return;
        setSession(null);
        const { status } = toAuthError(error);
        if (status === 401 || status === 403) {
          clearStoredAuthSession();
          resetSessionAnalytics();
          setIsHydrated(true);
        } else {
          setSessionError(true);
        }
      });
  }, [persistSession, resetSessionAnalytics, setSession]);

  useEffect(() => {
    const cached = getStoredAuthSession();
    if (cached) {
      setSession(cached);
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;
      const nextSession = getStoredAuthSession();
      if (nextSession && hasSameSessionScope(sessionRef.current, nextSession)) return;
      if (nextSession) hasAnalyticsSessionRef.current = true;
      void notifyBeforeAuthChange();
      setSession(null);
      if (!nextSession) {
        authRevisionRef.current += 1;
        setSessionError(false);
        setIsHydrated(true);
        resetSessionAnalytics();
        return;
      }
      refreshSession();
    };

    refreshSession();
    window.addEventListener("storage", onStorage);

    return () => {
      authRevisionRef.current += 1;
      window.removeEventListener("storage", onStorage);
    };
  }, [notifyBeforeAuthChange, refreshSession, resetSessionAnalytics, setSession]);

  const applySession = useCallback(
    (nextSession: AuthSession) => {
      void notifyBeforeAuthChange();
      authRevisionRef.current += 1;
      persistSession(nextSession);
      setIsHydrated(true);
      setSessionError(false);
    },
    [notifyBeforeAuthChange, persistSession],
  );

  const updateUser = useCallback(
    (patch: Partial<AuthSession["user"]>) => {
      const current = sessionRef.current;
      if (!current || !hasSameSessionScope(session, current)) return;
      const next: AuthSession = { ...current, user: { ...current.user, ...patch } };
      if (!hasSameSessionScope(current, next)) {
        void notifyBeforeAuthChange();
        authRevisionRef.current += 1;
      }
      persistSession(next);
    },
    [notifyBeforeAuthChange, persistSession, session],
  );

  const login = useCallback(
    async (payload: LoginPayload) => {
      const revision = ++loginRevisionRef.current;
      setLoginState({ isPending: true, error: null });
      try {
        const nextSession = await loginV2(payload);
        applySession(nextSession);
        if (revision === loginRevisionRef.current) setLoginState({ isPending: false, error: null });
        return nextSession;
      } catch (error) {
        if (revision === loginRevisionRef.current) setLoginState({ isPending: false, error });
        throw error;
      }
    },
    [applySession],
  );

  const resetLoginError = useCallback(() => {
    loginRevisionRef.current += 1;
    setLoginState({ isPending: false, error: null });
  }, []);

  const logout = useCallback(async () => {
    await Promise.race([notifyBeforeAuthChange(), new Promise<void>((resolve) => window.setTimeout(resolve, 1000))]);
    authRevisionRef.current += 1;
    logoutRequest().catch(() => {
      // Ignore network/auth errors during logout — local cleanup is what matters.
    });
    clearStoredAuthSession();
    resetSessionAnalytics();
    setSession(null);
    setSessionError(false);
    setIsHydrated(true);
  }, [notifyBeforeAuthChange, resetSessionAnalytics, setSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isAuthenticated: Boolean(session?.user?.id),
      isHydrated,
      sessionError,
      retrySession: refreshSession,
      queryScope,
      login,
      isLoginPending: loginState.isPending,
      loginError: loginState.error instanceof Error ? loginState.error.message : null,
      resetLoginError,
      applySession,
      updateUser,
      logout,
      registerBeforeAuthChange,
      registerBeforeOrganizationChange,
      prepareForOrganizationChange,
      displayName: formatAuthUserName(session?.user),
    }),
    [
      applySession,
      isHydrated,
      login,
      loginState,
      logout,
      prepareForOrganizationChange,
      registerBeforeAuthChange,
      registerBeforeOrganizationChange,
      queryScope,
      refreshSession,
      resetLoginError,
      session,
      sessionError,
      updateUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
